import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { BusinessUnitViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { BusinessUnitAddEditComponent } from '../dialogs/business-unit-add-edit/business-unit-add-edit.component';
import { DictionaryExportService } from '../services/dictionary-export.service';

@Component({
    selector: 'app-business-units',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss'],
    standalone: false
})
export class BusinessUnitsComponent extends DictionarySimplePageComponent<BusinessUnitViewModel> {
  constructor(
    private readonly esraApiClient: EsraPipingAPIClient,
    private readonly dialog: MatDialog,
    private readonly snackBarService: SnackBarService,
    private readonly insightsService: InsightsService,
    private readonly dictionaryExportService: DictionaryExportService
  ) {
    super();
    this.exportFn = () => this.dictionaryExportService.exportBusinessUnits();
  }

  protected childOnInitBefore(): void { /* do nothing */ }
  protected childOnInitAfter(): void { /* do nothing */ }

  protected searchAll(): void {
    // Use the paged endpoint with showAll=true to load both active and inactive
    // records. The client-side filter predicate handles active/inactive visibility.
    // getAllCurrentBusinessUnits() only returns active items (it is a dropdown feed)
    // and would hide inactive records from the admin grid entirely.
    this.esraApiClient.getAllBusinessUnitsPaged(undefined, undefined, undefined, true)
      .pipe(
        indicate(this.searching$),
        first(),
        tap((businessUnits: BusinessUnitViewModel[]) => {
          this.totalCount = businessUnits.length;
          this.dataSource.data = businessUnits;
        })
      ).subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<BusinessUnitViewModel> {
    return {
      displayStatusColumn: true,
      displayDeleteButton: true,
      dictionaryItems: [
        new DictionaryItem('Last Updated',  'updated', 'standard', (item: BusinessUnitViewModel) => this.getDate(item)),
        new DictionaryItem('Business Unit', 'name',    'extended', undefined,
          (item: BusinessUnitViewModel) => item.alwaysAvailableForEveryUser ? 'Always available' : null)
      ]
    };
  }

  protected getDate(item: BusinessUnitViewModel): string {
    return String(item.updated.getMonth() + 1).padStart(2, '0') + '/' +
           String(item.updated.getDate()).padStart(2, '0') + '/' +
           item.updated.getFullYear();
  }

  protected getFilterPredicate(record: BusinessUnitViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery: string = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const isActiveFilter: string = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    const matchesSearchQuery: boolean =
      (record.name?.toLowerCase().includes(searchQuery) ?? false) ||
      record.updated.toString().toLowerCase().includes(searchQuery);

    if (isActiveFilter == null) {
      return matchesSearchQuery;
    } else {
      return matchesSearchQuery && record.isActive === JSON.parse(isActiveFilter);
    }
  }

  protected getNoMatchMessage(): string {
    return 'No business units match provided search criteria';
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(businessUnit: BusinessUnitViewModel): void {
    this.openEditor(businessUnit);
  }

  protected editItemReferenced(businessUnit: BusinessUnitViewModel): void {
    // Referenced business units can still be edited — only deactivation is blocked.
    this.editItem(businessUnit);
  }

  private openEditor(businessUnit: BusinessUnitViewModel | null): void {
    const dialogRef: MatDialogRef<BusinessUnitAddEditComponent, boolean> = this.dialog.open<
      BusinessUnitAddEditComponent,
      BusinessUnitViewModel | null,
      boolean
    >(BusinessUnitAddEditComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: businessUnit
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.searchAll();
      }
    });
  }

  protected deleteItem(item: BusinessUnitViewModel): void {
    const hasAssociations = (item as any)['hasAssociations'];

    if (hasAssociations) {
      this.snackBarService.showSnackBar(
        true,
        `Business unit "${item.name}" could not be deleted because it has associated items.`,
        'mat-snack-bar-error'
      );
      return;
    }

    this.esraApiClient.deleteBusinessUnit(item.id).subscribe({
      next: () => {
        const index: number = this.dataSource.data.findIndex(b => b.id === item.id);
        if (index !== -1) {
          this.dataSource.data.splice(index, 1);
          this.dataSource.data = [...this.dataSource.data];
        }
        this.snackBarService.showSnackBar(
          true,
          `Business unit "${item.name}" was successfully deleted.`,
          'mat-snack-bar-info'
        );
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong deleting business unit "${item.name}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected updateStatus(item: BusinessUnitViewModel): void {
    this.esraApiClient.toggleBusinessUnitStatus(item.id).subscribe({
      next: (updated: BusinessUnitViewModel) => {
        const index: number = this.dataSource.data.findIndex(b => b.id === item.id);
        if (index !== -1) {
          this.dataSource.data[index] = updated;
          this.dataSource.data = [...this.dataSource.data];
        }
        const newState: string = item.isActive ? 'deactivated' : 'activated';
        this.snackBarService.showSnackBar(
          true,
          `Business unit "${item.name}" was successfully ${newState}.`,
          'mat-snack-bar-info'
        );
        this.search();
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong toggling business unit "${item.name}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected getUsageInformation(_item: object): void { /* not applicable for business units */ }
}
